import { Box, TableCell, TableRow, Typography } from '@mui/material';
import { Control, Controller, UseFormSetValue, useWatch } from 'react-hook-form';
import { DeviceAlertComparisonType, DeviceAlertState } from 'src/__generated__/graphql';
import { FormInputText, FormSelect } from 'src/components/form';
import { FormValues } from './updateSchema';
import { useEffect } from 'react';
import Checkbox from '@mui/material/Checkbox';

const colorsHue = {
  ok: '#2A3D44',
  critical: '#402634',
  warning: '#3E322F',
};

type SupportedAlertState = Exclude<DeviceAlertState, DeviceAlertState.Info>;

type AlertStateRowProps = {
  control: Control<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  alertState: SupportedAlertState;
}

const rangeComparisonTypes = [
  DeviceAlertComparisonType.InsideRange,
  DeviceAlertComparisonType.OutsideRange,
];

const thresholdComparisonTypes = [
  DeviceAlertComparisonType.Above,
  DeviceAlertComparisonType.Below,
];

const AlertStateRow = (props: AlertStateRowProps) => {
  const configs: Record<
    SupportedAlertState,
    {
      backgroundColor: string;
      title: string;
      formName: 'ok' | 'warning' | 'critical';
    }
  > = {
    [DeviceAlertState.Ok]: {
      title: 'OK',
      backgroundColor: colorsHue.ok,
      formName: 'ok',
    },
    [DeviceAlertState.Warning]: {
      title: 'WARNING',
      backgroundColor: colorsHue.warning,
      formName: 'warning',
    },
    [DeviceAlertState.Critical]: {
      title: 'CRITICAL',
      backgroundColor: colorsHue.critical,
      formName: 'critical',
    },
  };
  const { alertState, control, setValue } = props;

  const { backgroundColor, title, formName } = configs[alertState];

  const isEnabled = useWatch({
    control,
    name: `${formName}.enabled`
  })

  const comparisonType = useWatch({
    control,
    name: `${formName}.comparisonType`,
  });
  useEffect(() => {
  if (thresholdComparisonTypes.includes(comparisonType)) {
    setValue(`${configs[alertState].formName}.thresholdMin`, null);
    setValue(`${configs[alertState].formName}.thresholdMax`, null);
  } else if (rangeComparisonTypes.includes(comparisonType)) {
    setValue(`${configs[alertState].formName}.thresholdValue`, null);
  }
  // Si quieres limpiar otros campos, agrégalos aquí
}, [comparisonType, alertState, setValue]);

  const criteriaOptions = [
    {
      label: 'Sobre el valor',
      value: DeviceAlertComparisonType.Above,
    },
    {
      label: 'Bajo el valor',
      value: DeviceAlertComparisonType.Below,
    },
    {
      label: 'Entre los valores',
      value: DeviceAlertComparisonType.InsideRange,
    },
    {
      label: 'Fuera de los valores',
      value: DeviceAlertComparisonType.OutsideRange,
    },
  ];
  return (
    <TableRow sx={{ backgroundColor }}>
      <TableCell align="left">
        <Typography variant="h6">{title}</Typography>
      </TableCell>
      <TableCell align="left" sx={{ verticalAlign: 'top' }}>
        <FormInputText
          control={control}
          name={`${formName}.eventName`}
          label="Nombre"
          helperText=" "
          disabled={!isEnabled}
        />
      </TableCell>
      <TableCell align="left" sx={{ verticalAlign: 'top' }}>
        <FormInputText
          control={control}
          name={`${formName}.notificationDelaySec`}
          type="number"
          label="Cadencia (s)"
          disabled={!isEnabled}
        />
      </TableCell>
      <TableCell align="left" sx={{ verticalAlign: 'top' }}>
        <FormSelect
          control={control}
          name={`${formName}.comparisonType`}
          options={criteriaOptions}
          label="Criterio"
          disabled={!isEnabled}
        />
      </TableCell>
      <TableCell sx={{ verticalAlign: 'top' }}>
        {comparisonType != null &&
          rangeComparisonTypes.includes(comparisonType) && (
            <Box display="flex" gap={2}>
              <FormInputText
                control={control}
                name={`${formName}.thresholdMin`}
                type="number"
                label="Mínimo"
                disabled={!isEnabled}
              />
              <FormInputText
                control={control}
                name={`${formName}.thresholdMax`}
                type="number"
                label="Máximo"
                disabled={!isEnabled}
              />
            </Box>
          )}
        {comparisonType != null &&
          thresholdComparisonTypes.includes(comparisonType) && (
            <FormInputText
              control={control}
              name={`${formName}.thresholdValue`}
              type="number"
              label="Valor"
              disabled={!isEnabled}
            />
          )}
      </TableCell>
      <TableCell>
        <Controller
          name={`${formName}.enabled`}
          control={control}
          render={({ field }) => (
            <Checkbox
              checked={!!field.value}
              onChange={(e) => field.onChange(e.target.checked)}
            />
          )}
        />
      </TableCell>
    </TableRow>
  );
};

export { AlertStateRow };
